import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Grid,
  TextField,
  Button,
  LinearProgress,
  InputAdornment
} from '@mui/material';
import {
  HealthAndSafety as ComplianceIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { authGet, authPut, authPost } from '../../services/apiService';
import { format } from 'date-fns';

const darkGreenColor = "#1D4434";

/**
 * HipaaCuiAdmin component for managing HIPAA/CUI compliance
 * @returns {JSX.Element} HipaaCuiAdmin component
 */
const HipaaCuiAdmin = () => {
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [configSuccess, setConfigSuccess] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  
  const [privacyControlEpicId, setPrivacyControlEpicId] = useState('');
  const [hipaaControlEpicId, setHipaaControlEpicId] = useState('');
  const [securityControlEpicId, setSecurityControlEpicId] = useState('');
  const [autoSyncIntervalHours, setAutoSyncIntervalHours] = useState(4);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      const response = await authGet('/api/hipaa-cui/config');
      
      if (response.success) {
        setPrivacyControlEpicId(response.config.privacyControlEpicId || '');
        setHipaaControlEpicId(response.config.hipaaControlEpicId || '');
        setSecurityControlEpicId(response.config.securityControlEpicId || '');
        setAutoSyncIntervalHours(response.config.autoSyncIntervalHours || 4);
        setLastSync(response.config.lastSync || null);
      } else {
        setConfigError('Failed to load configuration');
      }
    } catch (error) {
      console.error('Error loading HIPAA/CUI config:', error);
      setConfigError(error.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);
      setConfigError(null);
      setConfigSuccess(null);
      
      const response = await authPut('/api/hipaa-cui/config', {
        privacyControlEpicId: privacyControlEpicId.trim(),
        hipaaControlEpicId: hipaaControlEpicId.trim(),
        securityControlEpicId: securityControlEpicId.trim(),
        autoSyncIntervalHours: parseInt(autoSyncIntervalHours) || 4
      });
      
      if (response.success) {
        setConfigSuccess('Configuration saved successfully');
        setLastSync(response.config.lastSync || null);
        setTimeout(() => setConfigSuccess(null), 5000);
        await loadConfig(); // Refresh to get updated timestamps
      } else {
        setConfigError(response.message || 'Failed to save configuration');
      }
    } catch (err) {
      console.error('Error saving HIPAA/CUI config:', err);
      setConfigError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleForceSync = async () => {
    try {
      setSyncing(true);
      setConfigError(null);
      setConfigSuccess(null);
      setSyncResult(null);
      
      const response = await authPost('/api/hipaa-cui/sync');
      
      if (response.success) {
        setSyncResult(response.summary);
        setConfigSuccess('HIPAA/CUI progress refreshed from Work Items.');
        setLastSync(new Date().toISOString());
        setTimeout(() => setConfigSuccess(null), 10000);
        await loadConfig(); // Refresh to get updated timestamps
      } else {
        setConfigError(response.error || response.message || 'Failed to sync HIPAA/CUI data');
      }
    } catch (err) {
      console.error('Error syncing HIPAA/CUI data:', err);
      setConfigError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to sync HIPAA/CUI data');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <SettingsIcon sx={{ mr: 1, color: darkGreenColor }} />
        <Typography variant="h5" component="h1">
          HIPAA/CUI Configuration
        </Typography>
      </Box>

      {loadingConfig ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Azure DevOps Epic Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure Azure DevOps Epic IDs for different compliance controls. These Epics will be used for tracking and reporting HIPAA/CUI compliance.
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Privacy Control Epic ID"
                    value={privacyControlEpicId}
                    onChange={(e) => setPrivacyControlEpicId(e.target.value)}
                    placeholder="Enter Azure DevOps Epic ID"
                    helperText="Azure DevOps Epic ID for Privacy Control"
                    disabled={loadingConfig || savingConfig || syncing}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="HIPAA Control Epic ID"
                    value={hipaaControlEpicId}
                    onChange={(e) => setHipaaControlEpicId(e.target.value)}
                    placeholder="Enter Azure DevOps Epic ID"
                    helperText="Azure DevOps Epic ID for HIPAA Control"
                    disabled={loadingConfig || savingConfig || syncing}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Security Control Epic ID"
                    value={securityControlEpicId}
                    onChange={(e) => setSecurityControlEpicId(e.target.value)}
                    placeholder="Enter Azure DevOps Epic ID"
                    helperText="Azure DevOps Epic ID for Security Control"
                    disabled={loadingConfig || savingConfig || syncing}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Auto Sync Interval"
                    value={autoSyncIntervalHours}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 4;
                      if (value >= 1 && value <= 168) {
                        setAutoSyncIntervalHours(value);
                      }
                    }}
                    inputProps={{ min: 1, max: 168, step: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hours</InputAdornment>
                    }}
                    helperText="Hours between automatic syncs from ADO (1-168, default: 4)"
                    disabled={loadingConfig || savingConfig || syncing}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ height: '100%' }}>
                    {lastSync ? (
                      <>
                        <CheckCircleIcon color="success" />
                        <Typography variant="body2" color="text.secondary">
                          Last synced: {format(new Date(lastSync), 'PPpp')}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ErrorIcon color="warning" />
                        <Typography variant="body2" color="text.secondary">
                          Never synced
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Button
                      variant="contained"
                      onClick={handleSaveConfig}
                      disabled={savingConfig || loadingConfig || syncing}
                      startIcon={savingConfig ? <LinearProgress size={20} /> : <SaveIcon />}
                      sx={{
                        backgroundColor: darkGreenColor,
                        '&:hover': { backgroundColor: '#0f2e1f' }
                      }}
                    >
                      {savingConfig ? 'Saving...' : 'Save Configuration'}
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleForceSync}
                      disabled={syncing || loadingConfig}
                      startIcon={syncing ? <LinearProgress size={20} /> : <SyncIcon />}
                    >
                      {syncing ? 'Refreshing...' : 'Refresh from Work Items'}
                    </Button>

                  </Box>
                </Grid>
              </Grid>

              {configSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {configSuccess}
                </Alert>
              )}

              {configError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {configError}
                </Alert>
              )}

              {syncResult && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    HIPAA/CUI data synced successfully!
                  </Alert>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Sync Summary
                    </Typography>
                    <Grid container spacing={2}>
                      {syncResult.totalWorkItems !== undefined && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Total Work Items
                          </Typography>
                          <Typography variant="h6">
                            {syncResult.totalWorkItems?.toLocaleString() || 0}
                          </Typography>
                        </Grid>
                      )}
                      {syncResult.privacyControlItems !== undefined && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Privacy Control Items
                          </Typography>
                          <Typography variant="h6">
                            {syncResult.privacyControlItems?.toLocaleString() || 0}
                          </Typography>
                        </Grid>
                      )}
                      {syncResult.hipaaControlItems !== undefined && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            HIPAA Control Items
                          </Typography>
                          <Typography variant="h6">
                            {syncResult.hipaaControlItems?.toLocaleString() || 0}
                          </Typography>
                        </Grid>
                      )}
                      {syncResult.securityControlItems !== undefined && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="text.secondary">
                            Security Control Items
                          </Typography>
                          <Typography variant="h6">
                            {syncResult.securityControlItems?.toLocaleString() || 0}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Box>
              )}

              <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'info.50' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  About HIPAA/CUI Configuration:
                </Typography>
                <Typography variant="body2" paragraph>
                  Configure Azure DevOps Epic IDs for different compliance controls. The system will automatically sync work items from these Epics based on the configured interval.
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  <li><Typography variant="body2">Privacy Control: Epic ID for privacy-related compliance tracking</Typography></li>
                  <li><Typography variant="body2">HIPAA Control: Epic ID for HIPAA compliance tracking</Typography></li>
                  <li><Typography variant="body2">Security Control: Epic ID for security compliance tracking</Typography></li>
                  <li><Typography variant="body2">Auto Sync: Automatically syncs data from Azure DevOps at the configured interval</Typography></li>
                </Box>
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default HipaaCuiAdmin;

